import React, { useState, useMemo } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { CONSTANTS } from "../constants/gameConstants";
import { ASSETS } from "../assetManifest";

type TroopType = keyof typeof CONSTANTS.TROOPS;
type EnemyType = keyof typeof CONSTANTS.ENEMIES;

const enemyList = Object.entries(CONSTANTS.ENEMIES) as [EnemyType, typeof CONSTANTS.ENEMIES[EnemyType]][];

const StatDisplay = ({ label, value, isDiscovered }: { label: string, value: string | number | undefined, isDiscovered: boolean }) => {
    if (value === undefined || (value === 0 && label !== 'Movement Speed')) return null;
    return (
        <div className="flex justify-between text-lg py-2 border-b border-gray-600">
            <span className="text-gray-400">{label}</span>
            <span className="font-bold">{isDiscovered ? value : '???'}</span>
        </div>
    );
};

export function CodexModal({ onClose, discoveredTroops }: { onClose: () => void, discoveredTroops: Set<TroopType> }) {
    const [activeTab, setActiveTab] = useState<'troops' | 'enemies'>('troops');
    const [troopIndex, setTroopIndex] = useState(0);
    const [enemyIndex, setEnemyIndex] = useState(0);

    const troopList = useMemo(() => {
        return (Object.entries(CONSTANTS.TROOPS) as [TroopType, typeof CONSTANTS.TROOPS[TroopType]][])
            .sort(([keyA, troopA], [keyB, troopB]) => {
                const isADiscovered = discoveredTroops.has(keyA);
                const isBDiscovered = discoveredTroops.has(keyB);

                if (isADiscovered && !isBDiscovered) return -1;
                if (!isADiscovered && isBDiscovered) return 1;

                const costA = troopA.cost;
                const costB = troopB.cost;

                if (costA > 0 && costB === 0) return -1;
                if (costA === 0 && costB > 0) return 1;

                if (costA > 0 && costB > 0) {
                    if (costA !== costB) return costA - costB;
                }

                const nameA = troopA.name || keyA;
                const nameB = troopB.name || keyB;
                return nameA.localeCompare(nameB);
            });
    }, [discoveredTroops]);

    const handleNext = () => {
        if (activeTab === 'troops') {
            setTroopIndex((prev) => (prev + 1) % troopList.length);
        } else {
            setEnemyIndex((prev) => (prev + 1) % enemyList.length);
        }
    };

    const handlePrev = () => {
        if (activeTab === 'troops') {
            setTroopIndex((prev) => (prev - 1 + troopList.length) % troopList.length);
        } else {
            setEnemyIndex((prev) => (prev - 1 + enemyList.length) % enemyList.length);
        }
    };

    const currentItem = activeTab === 'troops' ? troopList[troopIndex] : enemyList[enemyIndex];
    const [key, data] = currentItem;
    const isDiscovered = activeTab === 'troops' ? discoveredTroops.has(key as TroopType) : true; // Enemies are always discovered
    const name = 'name' in data ? data.name : key.charAt(0).toUpperCase() + key.slice(1);
    const description = 'description' in data && data.description ? data.description : "No information available.";
    
    let imageKey: keyof typeof ASSETS.sprites;
    if (activeTab === 'troops') {
        imageKey = key as keyof typeof ASSETS.sprites;
    } else {
        if (key === 'basic') {
            imageKey = 'enemy';
        } else {
            imageKey = `enemy_${key}` as keyof typeof ASSETS.sprites;
        }
    }
    const imagePath = ASSETS.sprites[imageKey]?.path;

    let generationInfo = null;
    if (activeTab === 'troops') {
        const troopKey = key as TroopType;
        const troopData = data as typeof CONSTANTS.TROOPS[TroopType];
        if (troopData.cost === 0) {
            const recipe = CONSTANTS.MERGE_COMBINATIONS[troopKey as keyof typeof CONSTANTS.MERGE_COMBINATIONS];
            if (recipe) {
                const component1Data = CONSTANTS.TROOPS[recipe[0]];
                const component2Data = CONSTANTS.TROOPS[recipe[1]];
                const component1Name = component1Data.name || recipe[0];
                const component2Name = component2Data.name || recipe[1];
                
                const component1Image = ASSETS.sprites[recipe[0] as keyof typeof ASSETS.sprites]?.path;
                const component2Image = ASSETS.sprites[recipe[1] as keyof typeof ASSETS.sprites]?.path;

                const isComponent1Discovered = discoveredTroops.has(recipe[0]);
                const isComponent2Discovered = discoveredTroops.has(recipe[1]);

                generationInfo = (
                    <div className="flex flex-col items-center justify-center gap-1 text-lg">
                        <div className="flex flex-col items-center text-center w-24">
                            {component1Image && <img src={component1Image} alt={isComponent1Discovered ? component1Name : '???'} className="w-12 h-12 mb-1" style={!isComponent1Discovered ? { filter: 'brightness(0)' } : {}} />}
                            <span className="text-sm capitalize">{isComponent1Discovered ? component1Name : '???'}</span>
                        </div>
                        <span className="text-yellow-400 font-bold my-1 text-2xl">+</span>
                        <div className="flex flex-col items-center text-center w-24">
                            {component2Image && <img src={component2Image} alt={isComponent2Discovered ? component2Name : '???'} className="w-12 h-12 mb-1" style={!isComponent2Discovered ? { filter: 'brightness(0)' } : {}} />}
                            <span className="text-sm capitalize">{isComponent2Discovered ? component2Name : '???'}</span>
                        </div>
                    </div>
                );
            } else {
                generationInfo = <p className="text-gray-400">A mysterious entity.</p>;
            }
        }
    }

    return (
        <div className="absolute inset-0 bg-black/60 flex items-center justify-center pointer-events-auto z-50">
            <div className="bg-gray-800 p-6 rounded-xl shadow-2xl text-white border-2 border-gray-700 w-full max-w-lg relative">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white">
                    <X size={28} />
                </button>

                <div className="flex border-b border-gray-600 mb-4">
                    <button
                        onClick={() => setActiveTab('troops')}
                        className={`px-6 py-3 text-xl font-bold ${activeTab === 'troops' ? 'text-yellow-400 border-b-2 border-yellow-400' : 'text-gray-400'}`}
                    >
                        Troops
                    </button>
                    <button
                        onClick={() => setActiveTab('enemies')}
                        className={`px-6 py-3 text-xl font-bold ${activeTab === 'enemies' ? 'text-yellow-400 border-b-2 border-yellow-400' : 'text-gray-400'}`}
                    >
                        Enemies
                    </button>
                </div>

                <div className="flex items-center justify-between mb-4">
                    <button onClick={handlePrev} className="p-2 rounded-full bg-gray-700 hover:bg-gray-600">
                        <ChevronLeft size={24} />
                    </button>
                    <h2 className="text-3xl font-bold text-center capitalize">{isDiscovered ? name : '???'}</h2>
                    <button onClick={handleNext} className="p-2 rounded-full bg-gray-700 hover:bg-gray-600">
                        <ChevronRight size={24} />
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <div className="flex justify-center items-center bg-gray-900/50 rounded-lg p-4 mb-4 h-48">
                            {imagePath && <img src={imagePath} alt={isDiscovered ? name : '???'} className="max-h-full max-w-full object-contain" style={!isDiscovered ? { filter: 'brightness(0)' } : {}} />}
                        </div>
                        <div className="space-y-2">
                            {'health' in data && <StatDisplay label="Health" value={data.health} isDiscovered={isDiscovered} />}
                            {'damage' in data && <StatDisplay label="Damage" value={data.damage} isDiscovered={isDiscovered} />}
                            {'range' in data && <StatDisplay label="Range" value={data.range} isDiscovered={isDiscovered} />}
                            {'attackCooldown' in data && <StatDisplay label="Attack Speed" value={data.attackCooldown} isDiscovered={isDiscovered} />}
                            {'speed' in data && <StatDisplay label="Movement Speed" value={data.speed} isDiscovered={isDiscovered} />}
                            {'cost' in data && data.cost > 0 && <StatDisplay label="Cost" value={data.cost} isDiscovered={isDiscovered} />}
                        </div>
                    </div>
                    <div className="flex flex-col space-y-4">
                        <div className="bg-gray-900/50 rounded-lg p-4 flex-grow">
                            <h3 className="text-lg font-bold text-yellow-400 mb-2 text-center">Description</h3>
                            <p className="text-gray-300 text-sm italic text-center">{isDiscovered ? description : "This unit has not been discovered yet."}</p>
                        </div>
                        {isDiscovered && generationInfo && (
                             <div className="bg-gray-900/50 rounded-lg p-4">
                                <h3 className="text-lg font-bold text-yellow-400 mb-2 text-center">Generation</h3>
                                {generationInfo}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
